if (!window.ProductGallery) {
class ProductGallery {
  constructor(container) {
    this.container = container || document.querySelector(".product-gallery");
    if (!this.container) return;

    this._ac = new AbortController();
    this._signal = this._ac.signal;
    this.mainWrapper = this.container.querySelector(
      ".product-gallery__main-wrapper"
    );
    this.mediaItems = this.container.querySelectorAll(".product__media-item");
    this.thumbnails = this.container.querySelectorAll(".thumbnail-button");
    this.currentIndex = 0;
    this.isDragging = false;
    this.startPos = 0;
    this.startYPos = null;
    this.currentTranslate = 0;
    this.prevTranslate = 0;
    this._resizeObserver = null;
    this._pswpInstance = null;

    this.init();
    this.initPhotoSwipe();
  }

  init() {
    const opts = { passive: false, signal: this._signal };
    const optsPassive = { signal: this._signal };

    // Main gallery navigation
    this.container.querySelectorAll(".gallery-nav").forEach((button) => {
      button.addEventListener(
        "click",
        () => {
          const direction = button.classList.contains("next") ? 1 : -1;
          this.moveToIndex(this.currentIndex + direction);
        },
        optsPassive
      );
    });

    // Thumbnail clicks
    this.thumbnails.forEach((thumb, index) => {
      thumb.addEventListener("click", () => this.moveToIndex(index), optsPassive);
    });

    // Touch events for main gallery
    this.mainWrapper.addEventListener(
      "touchstart",
      this.touchStart.bind(this),
      opts
    );
    this.mainWrapper.addEventListener("touchmove", this.touchMove.bind(this), opts);
    this.mainWrapper.addEventListener("touchend", this.touchEnd.bind(this), optsPassive);

    // Mouse events for main gallery
    if (window.innerWidth > 749) {
      this.mainWrapper.addEventListener(
        "mousedown",
        this.touchStart.bind(this),
        optsPassive
      );
      this.mainWrapper.addEventListener("mousemove", this.touchMove.bind(this), optsPassive);
      this.mainWrapper.addEventListener("mouseup", this.touchEnd.bind(this), optsPassive);
      this.mainWrapper.addEventListener("mouseleave", this.touchEnd.bind(this), optsPassive);
    }

    // Thumbnail scroll navigation
    const thumbsContainer = this.container.querySelector(
      ".product-gallery__thumbnails"
    );
    const thumbNavs = this.container.querySelectorAll(".thumbnail-nav");

    thumbNavs.forEach((nav) => {
      nav.addEventListener(
        "click",
        () => {
          const direction = nav.classList.contains("next") ? 1 : -1;
          thumbsContainer.scrollBy({ left: direction * 250, behavior: "smooth" });
        },
        optsPassive
      );
    });

    // Show/hide thumbnail navigation buttons
    const observerCallback = (entries) => {
      const thumbsWrapper = this.container.querySelector(
        ".product-gallery__thumbnails"
      );
      if (!thumbsWrapper) return;
      const showButtons = thumbsWrapper.scrollWidth > thumbsWrapper.clientWidth;
      thumbNavs.forEach(
        (nav) => (nav.style.display = showButtons ? "block" : "none")
      );
    };

    this._resizeObserver = new ResizeObserver(observerCallback);
    this._resizeObserver.observe(thumbsContainer);

    this.initPagination();
  }

  destroy() {
    if (!this.container) return;
    this._ac.abort();
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
    }
    this._resizeObserver = null;
    if (this._pswpInstance) {
      try {
        this._pswpInstance.close();
      } catch (e) {}
      this._pswpInstance = null;
    }
    this.container = null;
    this.mainWrapper = null;
    this.mediaItems = null;
    this.thumbnails = null;
    this.paginationDots = null;
  }

  moveToIndex(index) {
    const totalItems = this.mediaItems.length;
    this.currentIndex = Math.max(0, Math.min(index, totalItems - 1));

    // Update main gallery
    const offset = this.currentIndex * -100;
    this.mediaItems.forEach((item) => {
      item.style.transform = `translateX(${offset}%)`;
    });

    this.prevTranslate = offset;
    this.currentTranslate = offset;

    this.mediaItems.forEach((item, i) => {
      item.classList.toggle("is-active", i === this.currentIndex);
    });

    // Update thumbnails
    this.thumbnails.forEach((thumb, i) => {
      thumb.classList.toggle("is-active", i === this.currentIndex);
      thumb.setAttribute("aria-current", i === this.currentIndex);
    });

    // Ensure active thumbnail is visible
    const activeThumb = this.thumbnails[this.currentIndex];
    if (activeThumb && activeThumb.scrollIntoView) {
      activeThumb.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }

    if (this.paginationDots && this.paginationDots.length) {
      this.paginationDots.forEach((dot, i) => {
        dot.classList.toggle("is-active", i === this.currentIndex);
      });
    }
  }

  // Add setActiveMedia method for compatibility with standard media-gallery
  setActiveMedia(mediaId, prepend = false) {
    const targetIndex = Array.from(this.mediaItems).findIndex(item => 
      item.getAttribute('data-media-id') === mediaId
    );
    
    if (targetIndex !== -1) {
      this.moveToIndex(targetIndex);
    }
  }

  initPagination() {
    const container = this.container.querySelector(".product-gallery__pagination");
    if (!container) return;
    const count = this.mediaItems.length;
    const dots = [];
    container.innerHTML = "";
    for (let i = 0; i < count; i++) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "pagination-dot" + (i === 0 ? " is-active" : "");
      btn.setAttribute("aria-label", `Go to image ${i + 1}`);
      btn.addEventListener("click", () => this.moveToIndex(i), { signal: this._signal });
      container.appendChild(btn);
      dots.push(btn);
    }
    this.paginationDots = dots;
  }

  touchStart(event) {
    this.isDragging = true;
    this.startPos = event.type.includes("mouse")
      ? event.pageX
      : event.touches[0].clientX;
    this.currentTranslate = this.prevTranslate;
  }

  touchMove(event) {
    if (!this.isDragging) return;

    const currentPosition = event.type.includes("mouse")
      ? event.pageX
      : event.touches[0].clientX;

    // Get the Y position to detect vertical scrolling
    const currentYPosition = event.type.includes("mouse")
      ? event.pageY
      : event.touches[0].clientY;

    const diff = currentPosition - this.startPos;
    const move = Math.abs(diff);

    // Check if this is primarily a horizontal swipe
    // Get the initial Y position if we haven't stored it yet
    if (!this.startYPos) {
      this.startYPos = event.type.includes("mouse")
        ? event.pageY
        : event.touches[0].clientY;
    }

    // Calculate vertical movement
    const verticalDiff = Math.abs(currentYPosition - this.startYPos);
    
    // Only prevent default and handle as swipe if horizontal movement is greater than vertical
    // and greater than our minimum threshold
    if (move > 5 && move > verticalDiff) {
      // This is a horizontal swipe, prevent default scrolling
      event.preventDefault(); // Prevent page scrolling while swiping
      const containerWidth = this.mainWrapper.offsetWidth;
      this.currentTranslate =
        this.prevTranslate + (diff / containerWidth) * 100;

      // Apply the transform immediately for smooth dragging
      this.mediaItems.forEach((item) => {
        item.style.transform = `translateX(${this.currentTranslate}%)`;
      });
    }
  }

  touchEnd() {
    if (!this.isDragging) return;
    this.isDragging = false;
    // Reset the Y position tracking
    this.startYPos = null;

    const movedBy = this.currentTranslate - this.prevTranslate;

    if (Math.abs(movedBy) > 20) {
      // 20% threshold for swipe
      if (movedBy < 0) {
        this.moveToIndex(this.currentIndex + 1);
      } else {
        this.moveToIndex(this.currentIndex - 1);
      }
    } else {
      this.moveToIndex(this.currentIndex); // Snap back to current
    }
  }

  initPhotoSwipe() {
    const zoomButtons = this.container.querySelectorAll(".zoom-button");
    const galleryInstance = this;

    zoomButtons.forEach((button) => {
      button.addEventListener("click", (e) => {
        e.preventDefault();

        const pswpEl = document.querySelector(".pswp");
        if (!pswpEl || typeof window.PhotoSwipe === "undefined" || typeof window.PhotoSwipeUI_Default === "undefined") {
          return;
        }

        // Destroy existing gallery if it exists
        if (galleryInstance._pswpInstance) {
          galleryInstance._pswpInstance.close();
          galleryInstance._pswpInstance = null;
        }

        // Modified items mapping to exclude discount flags
        const items = Array.from(galleryInstance.mediaItems)
          .map((item) => {
            const img = item.querySelector("img:not(.discount-flag-img)"); // Exclude discount flags
            if (!img) return null;
            return {
              src: img.src.replace(/(_[0-9]+x[0-9]+)?\.jpg/, "_2048x2048.jpg"),
              w: 2048,
              h: 2048,
              msrc: img.src,
            };
          })
          .filter((item) => item !== null); // Remove any null items

        // getImageURLForShare: PhotoSwipe v4 以 options.getImageURLForShare() 调用，this 指向 options 而非实例，故用闭包中的 galleryInstance._pswpInstance
        const options = {
          index: Array.from(galleryInstance.mediaItems).indexOf(
            button.closest(".product__media-item")
          ),
          bgOpacity: 0.9,
          showHideOpacity: true,
          history: false,
          shareEl: true,
          shareButtons: [
            {
              id: "facebook",
              label: "Share on Facebook",
              url: "https://www.facebook.com/sharer/sharer.php?u={{url}}",
            },
            {
              id: "twitter",
              label: "Tweet",
              url: "https://twitter.com/intent/tweet?text={{text}}&url={{url}}",
            },
            {
              id: "pinterest",
              label: "Pin it",
              url: "http://www.pinterest.com/pin/create/button/?url={{url}}&media={{image_url}}&description={{text}}",
            },
          ],
          getImageURLForShare: function (shareButtonData) {
            const pswp = galleryInstance._pswpInstance;
            return (pswp && pswp.currItem && pswp.currItem.src) ? pswp.currItem.src : "";
          },
          getPageURLForShare: function (shareButtonData) {
            return window.location.href;
          },
          getTextForShare: function (shareButtonData) {
            return document.title;
          },

          // Add these options for thumbnails
          showAnimationDuration: 0,
          hideAnimationDuration: 0,
          galleryPIDs: true,
          galleryUID: "1",
          getThumbBoundsFn: (index) => {
            const thumbnail = galleryInstance.thumbnails[index];
            const img = thumbnail && thumbnail.querySelector("img");
            if (!img) return { x: 0, y: 0, w: 0 };
            const pageYScroll =
              window.pageYOffset || document.documentElement.scrollTop;
            const rect = img.getBoundingClientRect();
            return { x: rect.left, y: rect.top + pageYScroll, w: rect.width };
          },
        };

        const pswp = new window.PhotoSwipe(
          pswpEl,
          window.PhotoSwipeUI_Default,
          items,
          options
        );
        galleryInstance._pswpInstance = pswp;

        // Clear existing thumbnails before creating new ones
        pswp.listen("initialZoomInEnd", function () {
          const existingThumbs = document.querySelector(".pswp__thumbnails");
          if (existingThumbs) {
            existingThumbs.remove();
          }

          const container = document.createElement("div");
          container.className = "pswp__thumbnails";
          items.forEach((item, index) => {
            const thumb = document.createElement("div");
            thumb.className = "pswp__thumbnail";
            if (index === pswp.getCurrentIndex()) {
              thumb.classList.add("active");
            }
            thumb.innerHTML = `<img src="${item.msrc}" alt="Thumbnail ${
              index + 1
            }">`;
            thumb.addEventListener("click", () => {
              pswp.goTo(index);
            });
            container.appendChild(thumb);
          });
          pswp.scrollWrap.appendChild(container);
        });

        // Update thumbnail active states when image changes
        pswp.listen("beforeChange", function () {
          const currentIndex = pswp.getCurrentIndex();
          const thumbs = document.querySelectorAll(".pswp__thumbnail");
          thumbs.forEach((thumb, i) => {
            thumb.classList.toggle("active", i === currentIndex);
          });
        });

        // Clean up on close
        pswp.listen("destroy", function () {
          const thumbnailContainer =
            document.querySelector(".pswp__thumbnails");
          if (thumbnailContainer) {
            thumbnailContainer.remove();
          }
          galleryInstance._pswpInstance = null;
        });

        pswp.init();
      }, { signal: this._signal });
    });
  }
}

// Expose for quick-add modal and other dynamic usage
window.ProductGallery = ProductGallery;

// Initialize the gallery when the DOM is loaded
document.addEventListener("DOMContentLoaded", () => {
  const container = document.querySelector(".product-gallery");
  window.productGalleryInstance = container ? new ProductGallery(container) : null;
});

// Add zoom functionality
function initImageZoom() {
  // Add check for screen size
  if (window.innerWidth <= 749) return; // Exit if screen is too small

  const zoomResult = document.getElementById("global-zoom-result");
  const zoomWrappers = document.querySelectorAll(".image-zoom-wrapper");

  function setupZoom(wrapper) {
    // Add check for screen size
    if (window.innerWidth <= 749) return; // Exit if screen is too small

    const img = wrapper.querySelector("img");
    const lens = wrapper.querySelector(".image-zoom-lens");

    function moveLens(e) {
      let pos = getCursorPos(e);
      let x = pos.x - lens.offsetWidth / 2;
      let y = pos.y - lens.offsetHeight / 2;

      // Prevent lens from going outside the image
      if (x > wrapper.offsetWidth - lens.offsetWidth) {
        x = wrapper.offsetWidth - lens.offsetWidth;
      }
      if (x < 0) {
        x = 0;
      }
      if (y > wrapper.offsetHeight - lens.offsetHeight) {
        y = wrapper.offsetHeight - lens.offsetHeight;
      }
      if (y < 0) {
        y = 0;
      }

      // Set lens position
      lens.style.left = x + "px";
      lens.style.top = y + "px";

      // Calculate and set background position for result
      let cx = zoomResult.offsetWidth / lens.offsetWidth;
      let cy = zoomResult.offsetHeight / lens.offsetHeight;

      zoomResult.style.backgroundImage = "url('" + img.src + "')";
      zoomResult.style.backgroundSize =
        wrapper.offsetWidth * cx + "px " + wrapper.offsetHeight * cy + "px";
      zoomResult.style.backgroundPosition =
        "-" + x * cx + "px -" + y * cy + "px";
    }

    function getCursorPos(e) {
      let rect = wrapper.getBoundingClientRect();
      let x = e.pageX - rect.left - window.scrollX;
      let y = e.pageY - rect.top - window.scrollY;
      return { x, y };
    }

    // Mouse events
    wrapper.addEventListener("mouseenter", () => {
      document.body.classList.add("zoom-active");
    });

    wrapper.addEventListener("mouseleave", () => {
      document.body.classList.remove("zoom-active");
    });

    wrapper.addEventListener("mousemove", moveLens);
  }

  // Initialize zoom for all wrappers
  zoomWrappers.forEach(setupZoom);

  // Handle thumbnail clicks
  const thumbnailButtons = document.querySelectorAll(".thumbnail-button");
  thumbnailButtons.forEach((button) => {
    button.addEventListener("click", () => {
      setTimeout(() => {
        const activeWrapper = document.querySelector(
          ".product__media-item.is-active .image-zoom-wrapper"
        );
        if (activeWrapper) {
          // Remove old event listeners
          const oldWrapper = document.querySelector(".zoom-initialized");
          if (oldWrapper) {
            oldWrapper.classList.remove("zoom-initialized");
          }
          // Setup new zoom
          activeWrapper.classList.add("zoom-initialized");
          setupZoom(activeWrapper);
        }
      }, 300);
    });
  });

  // Add window resize handler
  window.addEventListener("resize", () => {
    if (window.innerWidth <= 749) {
      document.body.classList.remove("zoom-active");
    }
  });
}

// Initialize zoom functionality
document.addEventListener("DOMContentLoaded", initImageZoom);
}
